import serialize from 'form-serialize';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useAppContext } from 'src/components/app-context';
import { MarketSettings } from 'src/components/currency';

import { Translate } from 'src/components/translate';
import {
  ButtonIcon,
  ButtonText,
  ButtonType,
} from '@prisjakt/ui/button/variants';
import { CheckboxSwitch } from '@prisjakt/ui/checkbox/variants';
import { IconTrash } from '@prisjakt/ui/icon/trash';
import { BodySmallText, FontEmphasis } from '@prisjakt/ui/text/variants';
import { DropdownContent, DropdownToggle } from '@prisjakt/ui/dropdown';

import { isValidEmail } from 'src/areas/account/helpers';
import {
  removeAlertError,
  removeAlertRequest,
} from 'src/areas/sidebar/actions/alert';
import { CtxLink } from 'src/components/render-context/ctx-link';
import MultiRadio, {
  Variants,
} from 'src/components/review-form/components/multi-radio';
import { useIsAuthenticated } from 'src/hooks/use-is-authenticated';

import type { AppState } from 'src/reducers';

import type { AlertShopPreference, Maybe, UserReviewSummary } from 'src/types';
import type { ProductAlertFormData } from '../../types';
import { AlertTypes, Errors, ShopPreference } from './types';

import { EmailHint } from './email-hint-styles';
import {
  AlertForm,
  CancelButton,
  CheckboxNotify,
  EmailInput,
  NumberInput,
  PriceBelowWrapper,
  PriceStepInput,
  PriceStepWrapper,
  RatingWrapper,
  RemoveButton,
  SaveAlertButton,
  ShopsFilterWrapper,
  ShopPreferenceWrapper,
  SubmitButtonWrapper,
  TextInput,
  ShopSettingsLink,
  ShopPreferenceDropdown,
} from './styles';
import { Direction } from '@prisjakt/ui';
import { DropdownItem } from 'src/components/dropdown-item';

const getDefaultPriceThreshold = (
  regularPrice: number | null | undefined,
  minimumStepValue = 0.1,
  numberOfDecimals = 2
): string =>
  regularPrice
    ? (regularPrice - minimumStepValue).toFixed(numberOfDecimals)
    : '0';

const getDefaultPriceStep = (
  regularPrice: number | null | undefined, // Can this come from like the server??
  minimumStepValue = 0.1,
  numberOfDecimals = 2
): string => {
  const minDefaultPriceStep = minimumStepValue * 10;

  // Returns the largest value of 1% of regularPrice or minDefaultPriceStep
  return regularPrice
    ? Math.max(regularPrice * 0.01, minDefaultPriceStep).toFixed(
        numberOfDecimals
      )
    : String(minDefaultPriceStep);
};

type StoreQuery = {
  store?: Maybe<{
    userReviewSummary?: Maybe<UserReviewSummary>;
  }>;
};

const numberOfStoresAboveRating = (
  prices: Maybe<StoreQuery>[] | null | undefined,
  rating: number
): number => {
  if (!prices) {
    return -1;
  }

  return prices.filter(
    price => Number(price?.store?.userReviewSummary?.rating) > rating
  ).length;
};

export type ProductAlert = {
  alertId?: number;
  productId: number;
  email?: string;
  priceStep?: number;
  storePreference?: AlertShopPreference;
  storeGradeThreshold?: number;
  type: AlertTypes;
  priceThreshold?: number;
};

type OnCreate = (alert: ProductAlert) => void;

interface ProductAlertFormProps {
  productAlertFormData: ProductAlertFormData;
  onClose?: () => void;
  onCreate: OnCreate; // Remove? Should be alert type export type AlertCreateOptions = {
  privacyPolicyUri?: string;
  error?: string | boolean;
  isEdit?: boolean;
  shouldAutoFocus?: boolean;
  disabled?: boolean;
  showRemoveButton?: boolean;
}

export const ProductAlertForm = memo((props: ProductAlertFormProps) => {
  const {
    productAlertFormData,
    onClose,
    onCreate,
    privacyPolicyUri,
    error,
    isEdit = true,
    shouldAutoFocus,
    disabled,
    showRemoveButton = true,
  } = props;

  const { market, t } = useAppContext();

  const product = productAlertFormData.product;

  const userProductAlerts: {
    productId: number;
    alertId: number;
  }[] = useSelector((state: AppState) => state.areas.sidebar.alerts.products);

  const dispatch = useDispatch();

  const emailInput = useRef<HTMLInputElement>(null);

  const isAuthenticated = useIsAuthenticated();
  const { minimumStepValue, numberOfDecimals } = MarketSettings[market.code];

  const [errors, setErrors] = useState<Set<Errors>>(new Set());
  const [email, setEmail] = useState<string>('');
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);
  const [chosenStoreGradeThreshold, setChosenStoreGradeThreshold] =
    useState<number>((product?.alert?.storeGradeThreshold || 0) / 2);

  const [priceThreshold, setPriceThreshold] = useState<string>(() => {
    return product?.alert?.priceThreshold
      ? String(product.alert.priceThreshold)
      : getDefaultPriceThreshold(
          product?.priceSummary.regular,
          minimumStepValue,
          numberOfDecimals
        );
  });
  const [onlyAlertIfInStock, setOnlyAlertIfInStock] = useState<boolean>(
    product?.alert?.alertType !== AlertTypes.PRICE_DROPS
  );

  const [shopPreference, setShopPreference] = useState<AlertShopPreference>(
    product?.alert?.storePreference || ShopPreference.NONE
  );

  // Default to true
  const [priceStepCheckbox, setPriceStepCheckbox] = useState<boolean>(
    product?.alert ? !!product.alert.priceStep : true
  );

  const [priceStep, setPriceStep] = useState<string>(() => {
    if (product?.alert?.priceStep) {
      return String(product.alert.priceStep);
    }

    return getDefaultPriceStep(
      product?.priceSummary.regular,
      minimumStepValue,
      numberOfDecimals
    );
  });

  useEffect(() => {
    if (emailInput.current && shouldAutoFocus) {
      emailInput.current.focus();
    }

    return function cleanup() {
      dispatch(removeAlertError(undefined));
    };
  }, [dispatch, shouldAutoFocus]);

  const clearErrors = (): void => setErrors(new Set());

  const addError = (newError: Errors) => {
    setErrors(prevErrors => {
      const draftErrors = new Set<Errors>(prevErrors);
      return draftErrors.add(newError);
    });
  };

  const removeError = (errorToRemove: Errors) => {
    setErrors(prevErrors => {
      const draftErrors = new Set<Errors>(prevErrors);
      draftErrors.delete(errorToRemove);
      return draftErrors;
    });
  };

  useEffect(() => {
    if (!error) {
      return;
    }

    switch (error) {
      case Errors.ERROR_EMAIL_INVALID:
      case Errors.ERROR_EMAIL_IN_USE: {
        addError(error);
        break;
      }
      default: {
        addError(Errors.ERROR_UNKNOWN);
      }
    }
  }, [error]);

  const handlePriceThresholdChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPriceThreshold(event.target.value);

    if (!event.target.value || Number(event.target.value) < 0) {
      addError(Errors.ERROR_THRESHOLD_TOO_LOW);
    } else {
      removeError(Errors.ERROR_THRESHOLD_TOO_LOW);
    }
  };

  const handlePriceStepChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPriceStep(event.target.value);
  };

  const handleOnlyAlertIfInStockChange = () =>
    setOnlyAlertIfInStock(state => !state);

  const handlePriceStepCheckboxChange = () =>
    setPriceStepCheckbox(state => !state);

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    /* The error state is duplicated, between Redux and local state. This is due
    to the fact that we are using sagas to make the create alert request.
    The problem, apart from duplication, is that the error messages outlive the
    form since they are stored in redux. So the error handling in this form
    needs to be improved/simplified. But for now the code below is required to
    fix the issue with the email error messages not disappearing even though the
    user corrects the email.
    */
    if (isValidEmail(event.target.value)) {
      removeError(Errors.ERROR_EMAIL_INVALID);
    } else if (hasSubmitted) {
      addError(Errors.ERROR_EMAIL_INVALID);
    }
    dispatch(removeAlertError(undefined));
    removeError(Errors.ERROR_EMAIL_IN_USE);
    setEmail(event.target.value);
  };

  const saveAlert = useCallback(
    (event: React.FormEvent): void => {
      event.preventDefault();
      setHasSubmitted(true);

      const { rating = 0 } = serialize(event.target, {
        hash: true,
      });

      const userAlert = userProductAlerts.find(
        alert => alert.productId === product?.id
      );

      if (!product) {
        return;
      }

      clearErrors();

      if (!isAuthenticated && !isValidEmail(email)) {
        addError(Errors.ERROR_EMAIL_INVALID);
        if (shouldAutoFocus && emailInput.current) {
          emailInput.current.focus();
          emailInput.current.select();
        }
        return;
      }

      if (Number(priceThreshold) < 0 || Number(priceStep) < 0) {
        return;
      }

      const graphQLAlert = {
        type: onlyAlertIfInStock
          ? AlertTypes.IN_STOCK_PRICE_DROPS
          : AlertTypes.PRICE_DROPS,
        priceThreshold: Number(priceThreshold),
      };
      const priceStepValue = priceStepCheckbox ? Number(priceStep) : undefined;

      onCreate({
        alertId: userAlert && userAlert.alertId,
        productId: product.id,
        email: !isAuthenticated ? email : undefined,
        priceStep: priceStepValue,
        storePreference:
          shopPreference === ShopPreference.NONE ? undefined : shopPreference,
        storeGradeThreshold: rating ? Number(rating) * 2 : undefined,
        ...graphQLAlert,
      });
    },
    [
      email,
      onCreate,
      onlyAlertIfInStock,
      priceStep,
      priceStepCheckbox,
      priceThreshold,
      shopPreference,
      product,
      shouldAutoFocus,
      userProductAlerts,
      isAuthenticated,
    ]
  );

  const removeAlert = () => {
    const userAlert = userProductAlerts.find(
      alert => alert.productId === product?.id
    );

    if (!userAlert?.alertId) return;

    dispatch(removeAlertRequest(userAlert.alertId, product?.id));
    if (onClose) onClose();
  };

  const shopPreferenceOptions = [
    {
      name: t('alerts', `alerts.shop-preference-none`),
      value: ShopPreference.NONE,
    },
    {
      name: t('alerts', `alerts.shop-preference-exclude-disliked`),
      value: ShopPreference.EXCLUDE_DISLIKED,
    },
    {
      name: t('alerts', `alerts.shop-preference-liked`),
      value: ShopPreference.LIKED,
    },
  ];

  const renderTypeOptions = () => {
    return (
      <>
        <div>
          <PriceBelowWrapper>
            <BodySmallText emphasis={FontEmphasis.MEDIUM}>
              {t('alerts', 'type.price-below')}
            </BodySmallText>
            <NumberInput
              name="priceThreshold"
              ariaLabel={t('alerts', 'alert-form.price-limit')}
              warning={errors.has(Errors.ERROR_THRESHOLD_TOO_LOW)}
              value={priceThreshold}
              onChange={handlePriceThresholdChange}
              step={minimumStepValue}
              max={
                product?.priceSummary.regular
                  ? Number(
                      (product.priceSummary.regular - minimumStepValue).toFixed(
                        numberOfDecimals
                      )
                    )
                  : undefined
              }
              min={0}
              data-testid="price-threshold-number-input"
            />
          </PriceBelowWrapper>

          <PriceStepWrapper>
            <CheckboxSwitch
              text={t('alerts', 'alert-form.price-step-checkbox')}
              onChange={handlePriceStepCheckboxChange}
              checked={priceStepCheckbox}
            />
            <PriceStepInput>
              <NumberInput
                name="priceStep"
                ariaLabel={t('alerts', 'alert-form.price-interval')}
                data-testid="price-step-number-input"
                disabled={!priceStepCheckbox}
                warning={Number(priceStep) < 0}
                value={priceStep}
                onChange={handlePriceStepChange}
                step={minimumStepValue}
                max={
                  product?.priceSummary.regular
                    ? Number(
                        (
                          product.priceSummary.regular - minimumStepValue
                        ).toFixed(numberOfDecimals)
                      )
                    : undefined
                }
                min={0}
              />
            </PriceStepInput>
          </PriceStepWrapper>
        </div>

        <CheckboxNotify
          name="onlyAlertIfInStock"
          checked={onlyAlertIfInStock}
          text={t('alerts', 'alert-form.only-notify-in-stock')}
          onChange={handleOnlyAlertIfInStockChange}
        />

        <ShopsFilterWrapper>
          <BodySmallText emphasis={FontEmphasis.MEDIUM}>
            {t('alerts', 'alert-form.shops-with-higher-rating')}
          </BodySmallText>
          <RatingWrapper>
            <MultiRadio
              currentValue={
                product?.alert?.storeGradeThreshold
                  ? product.alert.storeGradeThreshold / 2
                  : null
              }
              disabledOptions={[5]}
              name="rating"
              onClick={e => {
                setChosenStoreGradeThreshold(Number(e.target.value));
              }}
              options={[1, 2, 3, 4, 5]}
              optionValues={[
                t('reviews', 'rating.terrible'),
                t('reviews', 'rating.bad'),
                t('reviews', 'rating.ok'),
                t('reviews', 'rating.good'),
                t('reviews', 'rating.excellent'),
              ]}
              required
              size="small"
              variant={Variants.rate}
            />
            <p>
              {product?.prices
                ? t('alerts', 'alert-form.n-stores', {
                    n: numberOfStoresAboveRating(
                      product?.prices?.nodes,
                      chosenStoreGradeThreshold
                    ),
                  })
                : null}
            </p>
          </RatingWrapper>

          {isAuthenticated && (
            <ShopPreferenceWrapper>
              <BodySmallText emphasis={FontEmphasis.MEDIUM}>
                {t('alerts', 'alert-form.filter-shops')}
              </BodySmallText>
              <ShopPreferenceDropdown closeOnItemClick>
                <DropdownToggle
                  showActiveIcon
                  type={ButtonType.TEXT}
                  disabled={!isAuthenticated}
                  containerProps={{ 'data-testid': 'ShopPreferenceSelector' }}
                >
                  {
                    shopPreferenceOptions.find(
                      row => row.value === shopPreference
                    )?.name
                  }
                </DropdownToggle>

                <DropdownContent placement={Direction.Up}>
                  {shopPreferenceOptions.map(option => {
                    return (
                      <DropdownItem
                        data-test="DropdownOption"
                        containerProps={{
                          'data-testid': option.value,
                        }}
                        key={option.value}
                        disabled={shopPreference === option.value}
                        onClick={() => setShopPreference(option.value)}
                      >
                        {option.name}
                      </DropdownItem>
                    );
                  })}
                </DropdownContent>
              </ShopPreferenceDropdown>

              <ShopSettingsLink to="/account/profile/shop-preference">
                {t('alerts', 'alert-form.shops-settings')}
              </ShopSettingsLink>
            </ShopPreferenceWrapper>
          )}
        </ShopsFilterWrapper>
      </>
    );
  };

  return (
    <AlertForm method="post" onSubmit={saveAlert} noValidate>
      {renderTypeOptions()}
      <EmailInput error={errors.has(Errors.ERROR_EMAIL_INVALID)}>
        {!isAuthenticated && (
          <>
            <TextInput
              type="email"
              name="email"
              inputRef={emailInput}
              defaultValue={email}
              onChange={handleEmailChange}
              placeholder={t('alerts', 'alert-form.email-placeholder')}
              tabIndex={0}
              autoFocus={shouldAutoFocus}
              // Do not send this element to CUX (cux.io)
              data-cux-exclude
            />
            <EmailHint visible={errors.has(Errors.ERROR_EMAIL_IN_USE)}>
              <Translate domain="alerts">
                alert-form.email-in-use-error
              </Translate>
              &nbsp;
              <CtxLink to="/login">
                <Translate domain="alerts">alert-form.login-error</Translate>
              </CtxLink>
            </EmailHint>
            <EmailHint error visible={errors.has(Errors.ERROR_EMAIL_INVALID)}>
              <Translate domain="alerts">
                alert-form.invalid-email-error
              </Translate>
            </EmailHint>
            <EmailHint error visible={errors.has(Errors.ERROR_UNKNOWN)}>
              <Translate domain="alerts">alert-form.unknown-error</Translate>
            </EmailHint>
            {privacyPolicyUri && (
              <EmailHint visible discreetLink>
                <CtxLink to={privacyPolicyUri}>
                  <Translate domain="product">
                    alert.how-your-email-is-used
                  </Translate>
                </CtxLink>
              </EmailHint>
            )}
          </>
        )}
      </EmailInput>

      {isEdit && showRemoveButton && (
        <RemoveButton onClick={removeAlert}>
          <ButtonIcon>
            <IconTrash />
          </ButtonIcon>
          <ButtonText>{t('product', 'alert.delete-button')}</ButtonText>
        </RemoveButton>
      )}

      <SubmitButtonWrapper>
        {isEdit && onClose && product && (
          <CancelButton onClick={onClose}>
            {t('alerts', 'alert-form.cancel-button')}
          </CancelButton>
        )}

        <SaveAlertButton
          disabled={disabled || errors.size > 0}
          containerProps={{
            'data-testid': 'CreateAlertButton',
            'data-test': 'CreateAlertButton',
          }}
          htmlType="submit"
          fullWidth
        >
          {t('alerts', 'alert-form.create-button')}
        </SaveAlertButton>
      </SubmitButtonWrapper>
    </AlertForm>
  );
});
